import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Debouncer, Queuer } from '@tanstack/pacer';
import { firstValueFrom } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';

import { environment } from '@fantaz/shared/app-config';
import { FzEventBus } from '@fantaz/shared/domain';

import { AuthServiceInjectionToken } from './auth';

// Type-safe WebSocket messages
export interface WebSocketMessage<T = any> {
  action: string;
  body: T;
  timestamp?: number;
}

export interface WebSocketCommand<T = any> {
  type: string;
  payload: T;
}

export enum WebSocketState {
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
  DISCONNECTED = 'DISCONNECTED',
  RECONNECTING = 'RECONNECTING',
  ERROR = 'ERROR',
}

type WebsocketMessage = {
  priority: number;
  [key: string]: unknown;
};

@Injectable({
  providedIn: 'root',
})
export class WebsocketService {
  readonly #authService = inject(AuthServiceInjectionToken);
  readonly #eventBus = inject(FzEventBus);
  readonly #destroyRef = inject(DestroyRef);

  subject$: WebSocketSubject<unknown> | undefined;
  private fingerprintId: number | undefined;

  // Connection state management
  readonly connectionState = signal<WebSocketState>(
    WebSocketState.DISCONNECTED,
  );

  private readonly CONFIG = {
    MAX_RETRY_ATTEMPTS: 10,
    BASE_DELAY: 1000,
    MAX_DELAY: 30000,
    HEARTBEAT_INTERVAL: 10000,
    HEARTBEAT_TIMEOUT: 5000,
    MAX_MISSED_HEARTBEATS: 3,
    RECONNECT_ON_USER_MESSAGE: true,
  } as const;

  // Reconnection configuration
  private retryCount = signal<number>(0);

  #logger = {
    debug: (...args: any[]) => {
      console.group('[WebSocketService]');
      console.debug(...args);
      console.groupEnd();
    },
    error: (...args: any[]) => {
      console.group('[WebSocketService]');
      console.error(...args);
      console.groupEnd();
    },
    warn: (...args: any[]) => {
      console.group('[WebSocketService]');
      console.warn(...args);
      console.groupEnd();
    },
    info: (...args: any[]) => {
      console.group('[WebSocketService]');
      console.info(...args);
      console.groupEnd();
    },
  };

  websocketMessageQueue = new Queuer<WebsocketMessage>(
    (item: WebsocketMessage) => {
      // Only process if connected, otherwise pause the queue
      if (this.isConnected && this.subject$) {
        this._sendWebsocketMessage(item);
      } else {
        this.#logger.warn(
          'Queue attempted to send while disconnected, pausing queue',
        );
        this.websocketMessageQueue.stop();
        this._tryReconnect();
      }
    },
    {
      getPriority: (n: WebsocketMessage) => n.priority,
    },
  );

  connectionDebouncer = new Debouncer(
    async () => {
      this.#logger.info('Running websocket debouncer');

      await this.connect();
    },
    {
      wait: () => this.calculateDelay(this.retryCount()),
    },
  );

  constructor() {
    this.#eventBus
      .on('websocket:send-message')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (event: any) => {
          this.#logger.debug('send message', event);
          this.sendMessage(event.data);
        },
        error: (error) => this.#logger.error('WebSocket send error:', error),
        complete: () => this.#logger.info('WebSocket send complete'),
      });

    // Cleanup on destroy
    this.#destroyRef.onDestroy(() => {
      this.cleanup();
    });
  }

  private get isDebugMode(): boolean {
    return !environment.production;
  }
  /**
   * Calculate the delay before the next reconnection attempt using exponential
   * backoff with jitter.
   * @param {number} attempt - The current reconnection attempt number.
   * @returns {number} - The calculated delay in milliseconds.
   */
  private calculateDelay(attempt: number): number {
    const baseDelay = 1000;
    const maxDelay = 30000;

    const exponentialDelay = Math.min(
      baseDelay * Math.pow(2, attempt),
      maxDelay,
    );

    const jitter = Math.random() * 1000;

    return exponentialDelay + jitter;
  }

  /**
   * Get the WebSocket URL for the current connection.
   *
   * @param {string} endpoint - Optional endpoint URL of the WebSocket.
   * @returns {Promise<string>} - The WebSocket URL for the current connection with fresh token.
   */
  private async _getWebSocketUrl(endpoint?: string): Promise<string> {
    this.#logger.debug(
      `Get WebSocket URL: ${endpoint ? endpoint : environment.webSocketURL}`,
    );

    // Get fresh token from auth session to ensure it's not expired
    const session = await firstValueFrom(
      this.#authService.getAuthSession({ forceRefresh: true }),
    );
    const id_token =
      session?.tokens?.idToken?.toString() ||
      this.#authService.getUserIdToken();
    const websocketUrl = endpoint || environment.webSocketURL;

    return `${websocketUrl}?Authorization=${id_token}&fps=${this.getFingerprint()}`;
  }

  /**
   * Handle the WebSocket connection opened event.
   */
  private _onConnected() {
    this.#logger.debug('Websocket connection opened');
    this._cancelReconnection();

    this.connectionState.set(WebSocketState.CONNECTED);

    this.#logger.debug('Websocket message queue started');
    this.websocketMessageQueue.start();
  }

  /**
   * Handle the WebSocket connection closed event.
   */
  private _onDisconnected() {
    this.#logger.warn('Websocket connection closed');
    this.subject$?.complete(); // Ensure proper cleanup
    this.subject$ = undefined;
    this.connectionState.set(WebSocketState.DISCONNECTED);

    this.websocketMessageQueue.stop();

    if (!this.websocketMessageQueue.store.state.isEmpty) {
      this.#logger.warn(
        'Websocket message queue has messages, try to reconnect',
      );

      this._tryReconnect();
    }
  }

  /**
   * Cancel the WebSocket reconnection attempt.
   */
  private _cancelReconnection() {
    this.#logger.debug('Cancel websocket reconnection');

    this.connectionDebouncer.cancel();
    this.retryCount.set(0);
  }

  private _handleWebSocketError(error: any) {
    this.#logger.error('WebSocket error:', error);
    this.connectionState.set(WebSocketState.ERROR);

    // Clear the subject on error
    this.subject$?.complete();
    this.subject$ = undefined;

    switch (error.code) {
      case 1006:
        this.#logger.warn('Abnormal closure - immediate reconnection');
        this._tryReconnect();
        break;
      case 1000:
        this.#logger.info('Normal closure - no reconnection');
        this._cancelReconnection();
        break;
      case 1001:
        this.#logger.warn('Server going away - delayed reconnection');
        setTimeout(() => this._tryReconnect(), 5000);
        break;
      default:
        this.#logger.error('Unknown error code:', error.code);
        this._tryReconnect();
    }

    this.#eventBus.cast('websocket:error', error);
  }

  /**
   * Try to reconnect
   */
  private _tryReconnect() {
    this.#logger.debug('Try websocket reconnection');

    // Only reconnect if we haven't exceeded max attempts
    if (this.retryCount() >= this.CONFIG.MAX_RETRY_ATTEMPTS) {
      this.#logger.error(
        `Max reconnection attempts (${this.CONFIG.MAX_RETRY_ATTEMPTS}) reached`,
      );
      this.connectionState.set(WebSocketState.ERROR);
      this.#eventBus.cast('websocket:max-reconnect-failed');
      return;
    }

    this.retryCount.update((count) => count + 1);
    this.connectionState.set(WebSocketState.RECONNECTING);
    this.connectionDebouncer.maybeExecute();
  }

  /**
   * Send a message through the WebSocket connection.
   * This should only be called when connection is verified by the queue processor.
   */
  private _sendWebsocketMessage(message: WebsocketMessage): void {
    if (!this.subject$ || !this.isConnected) {
      this.#logger.error(
        '_sendWebsocketMessage called without valid connection',
      );
      this.websocketMessageQueue.stop();
      this._tryReconnect();
      return;
    }

    try {
      this.subject$.next(message);
      this.#logger.debug('Message sent successfully');
    } catch (error) {
      this.#logger.error('Error sending message:', error);
      // Stop queue and reconnect on send error
      this.websocketMessageQueue.stop();
      this._tryReconnect();
    }
  }

  /**
   * Returns the current WebSocket connection.
   * @returns {WebSocketSubject<unknown> | undefined} - The WebSocketSubject for the established connection, or undefined if no connection exists.
   */
  get connection(): WebSocketSubject<unknown> | undefined {
    return this.subject$;
  }

  get isConnected(): boolean {
    return this.connectionState() === WebSocketState.CONNECTED;
  }

  get status(): WebSocketState {
    return this.connectionState();
  }

  /**
   * Establishes a WebSocket connection and returns a WebSocketSubject.
   * If a connection is already established and not closed, it returns the existing WebSocketSubject.
   * The connection is authenticated with the user token obtained from local storage.
   *
   * @param {string} endpoint - Optional endpoint URL of the WebSocket.
   * @returns {Promise<WebSocketSubject<unknown>>} - The WebSocketSubject for the established connection.
   */
  async connect(endpoint?: string): Promise<WebSocketSubject<unknown>> {
    this.#logger.debug(
      `${this.retryCount() > 0 ? 'Reconnecting' : 'Connecting'} to websocket`,
    );

    if (this.subject$) {
      this.#logger.debug('Websocket already connected');
      return this.subject$;
    }

    this.connectionState.set(
      this.retryCount() > 0
        ? WebSocketState.RECONNECTING
        : WebSocketState.CONNECTING,
    );

    const url = await this._getWebSocketUrl(endpoint);

    this.subject$ = webSocket({
      url,
      closeObserver: {
        next: () => {
          this._onDisconnected();
        },
      },
      openObserver: {
        next: () => {
          this._onConnected();
        },
      },
    });

    this.subject$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe({
      error: (err) => {
        this.#eventBus.cast('websocket:error', err);
      },
      complete: () => {
        this.#logger.info('WebSocket complete');
        this.#eventBus.cast('websocket:closed');
      },
      next: (message) => {
        this.#eventBus.cast<any>('websocket:message', message);

        this.#logger.debug('Websocket message received:', message);
      },
    });

    return this.subject$;
  }

  sendMessage(message: unknown, retry = false): void {
    this.#logger.debug('sendMessage called:', {
      hasSubject: !!this.subject$,
      connected: this.isConnected,
      retry,
      message,
    });

    this.websocketMessageQueue.addItem({
      priority: 1,
      ...(message as Partial<WebsocketMessage>),
    });

    // Automatically reconnect when user sends a message while disconnected
    // This will trigger token refresh via the debouncer
    if (!this.subject$) {
      this.#logger.info(
        'User sent message while disconnected - initiating reconnection with fresh token...',
      );
      this.connectionDebouncer.maybeExecute();
    }
  }

  on(): WebSocketSubject<unknown> | undefined {
    return this.subject$;
  }

  /**
   * Websocket connection close
   */
  close() {
    this.#logger.info('Closing websocket connection');

    if (this.subject$) {
      this.subject$.complete();
      this.subject$ = undefined;
    }

    this.connectionState.set(WebSocketState.DISCONNECTED);
    this._cancelReconnection();
  }

  /**
   * Complete cleanup on service destruction
   */
  private cleanup(): void {
    this.#logger.info('Cleaning up WebSocket service');
    this.close();
    this.websocketMessageQueue.stop();

    if (!this.websocketMessageQueue.store.state.isEmpty) {
      this.#logger.warn('Cleaning up with message');
    }
  }

  // https://github.com/darkskyapp/string-hash
  checksum(str: string) {
    let hash = 5381,
      i = str.length;

    while (i--) hash = (hash * 33) ^ str.charCodeAt(i);

    return hash >>> 0;
  }

  getFingerprint() {
    if (!this.fingerprintId) {
      this.fingerprintId = this.checksum(
        [
          navigator.userAgent,
          [screen.height, screen.width, screen.colorDepth].join('x'),
          new Date().getTimezoneOffset(),
          !!window.sessionStorage,
          !!window.localStorage,
          !!document.cookie,
        ].join('###'),
      );
    }

    return this.fingerprintId;
  }
}
